import type { DealerOptionItemDto, FactoryOptionDto } from '@api/machines'
import {
  SERVICE_CONTRACT_TYPES,
  type ServiceContractType,
  TRANSPORT_OPTION_TYPES,
  WizardErrorSteps
} from '@common/utils'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  type RulesToBeAppliedListType,
  type SectionsErrorsType,
  WizardSteps
} from '@/pages/customers/utils/interfaces'

export interface DealerOptionItemDtoWithChecked extends DealerOptionItemDto {
  checked: boolean
}
export interface FactoryOptionDtoWithChecked extends FactoryOptionDto {
  checked: boolean
}

interface BuilderStateType {
  activeStep: WizardSteps
  sectionsErrors: SectionsErrorsType
  selectedServiceContract: ServiceContractType
  selectedDealerOptions: DealerOptionItemDtoWithChecked[]
  rulesToBeApplied: RulesToBeAppliedListType
  archivedFactoryFits: FactoryOptionDtoWithChecked[]
  factoryFitsChecked: boolean
  expandedFamiliesOrGroups: Set<string>
  dealerFitsChecked: boolean
  selectedTransportType: string
  stepsWithError: WizardErrorSteps[]
  setStepsWithError: (val: WizardErrorSteps[]) => void
  setSectionsErrors: (val: Partial<SectionsErrorsType>) => void
  setSelectedTransportType: (value: string) => void
  setArchivedFactoryFits: (values: FactoryOptionDtoWithChecked[]) => void
  toggleFactoryFitsChecked: () => void
  toggleDealerFitsChecked: () => void
  openFactoryFitsSection: () => void
  openDealerFitsSection: () => void
  setRulesToBeApplied: (rules: RulesToBeAppliedListType) => void
  setExpandedFamiliesOrGroups: (value: string) => void
  expandFamiliesOrGroupsByKey: (value: string) => void
  closeExpandedFamilyOrGroup: (value: string) => void
  setSelectedDealerOptions: (
    value: DealerOptionItemDtoWithChecked[] | undefined
  ) => void
  setSelectedServiceContract: (value: ServiceContractType) => void
  setActiveStep: (step: WizardSteps) => void
  goNextStep: () => void
  goPrevStep: () => void
  updateSectionErrors: (errorFlag: keyof SectionsErrorsType) => void
  resetActiveStep: () => void
}

export const useEquipmentBuilderStore = create<BuilderStateType>()(
  devtools(
    set => ({
      activeStep: WizardSteps.OPTIONS,
      sectionsErrors: {
        serviceContract: false,
        transportOption: false,
        warranty: false
      },
      expandedFamiliesOrGroups: new Set<string>(),
      selectedTransportType: TRANSPORT_OPTION_TYPES.DELIVERY,
      selectedServiceContract: SERVICE_CONTRACT_TYPES.NONE,
      selectedDealerOptions: [],
      archivedFactoryFits: [],
      rulesToBeApplied: {},
      factoryFitsChecked: false,
      dealerFitsChecked: false,
      stepsWithError: [WizardErrorSteps.NO_ERROR],
      setStepsWithError: val => set({ stepsWithError: val }),
      setSectionsErrors: (val: Partial<SectionsErrorsType>) =>
        set(state => ({ sectionsErrors: { ...state.sectionsErrors, ...val } })),
      toggleDealerFitsChecked: () =>
        set(state => ({ dealerFitsChecked: !state.dealerFitsChecked })),
      toggleFactoryFitsChecked: () =>
        set(state => ({ factoryFitsChecked: !state.factoryFitsChecked })),
      openFactoryFitsSection: () => set({ factoryFitsChecked: true }),
      openDealerFitsSection: () => set({ dealerFitsChecked: true }),
      setSelectedTransportType: value => set({ selectedTransportType: value }),
      setRulesToBeApplied: rules => set({ rulesToBeApplied: rules }),
      setArchivedFactoryFits: values => set({ archivedFactoryFits: values }),
      setSelectedDealerOptions: value => set({ selectedDealerOptions: value }),
      closeExpandedFamilyOrGroup: item =>
        set(state => {
          const currentSet = new Set(state.expandedFamiliesOrGroups)
          currentSet.delete(item)
          return { expandedFamiliesOrGroups: currentSet }
        }),
      setExpandedFamiliesOrGroups: value =>
        set(state => {
          const currentSet = new Set(state.expandedFamiliesOrGroups)
          currentSet.add(value)
          return { expandedFamiliesOrGroups: currentSet }
        }),
      expandFamiliesOrGroupsByKey: key =>
        set(state => {
          const currentSet = new Set(state.expandedFamiliesOrGroups)
          if (currentSet.has(key)) {
            currentSet.delete(key)
          } else {
            currentSet.add(key)
          }
          return { expandedFamiliesOrGroups: currentSet }
        }),
      setSelectedServiceContract: value =>
        set({ selectedServiceContract: value }),
      setActiveStep: activeStep => set({ activeStep }),
      goNextStep: () => set(state => ({ activeStep: state.activeStep + 1 })),
      updateSectionErrors: errorFlag =>
        set(state => ({
          sectionsErrors: {
            ...state.sectionsErrors,
            [errorFlag]: false
          }
        })),
      goPrevStep: () =>
        set(state => {
          state.sectionsErrors.warranty && state.updateSectionErrors('warranty')
          state.sectionsErrors.serviceContract &&
            state.updateSectionErrors('serviceContract')
          state.sectionsErrors.transportOption &&
            state.updateSectionErrors('transportOption')
          return { activeStep: state.activeStep - 1 }
        }),
      resetActiveStep: () => set({ activeStep: WizardSteps.OPTIONS })
    }),
    { name: 'EquipmentBuilderStore' }
  )
)
